import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArchive,
  IconDots,
  IconPencil,
  IconPlus,
  IconUserPlus,
} from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchInput } from '@/components/SearchInput';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import { usePermissions } from '@/contexts/PermissionContext';
import type {
  GroupListItem,
  GroupSetupFormValues,
  GroupTemplateFormValues,
  GroupTemplateListItem,
} from '@/lib/groupTherapy';
import {
  ENROLLMENT_MODE_OPTIONS,
  GROUP_LIFECYCLE_STATUS_COLORS,
  GROUP_LIFECYCLE_STATUS_LABELS,
  GROUP_LIFECYCLE_STATUS_OPTIONS,
  createGroupTemplate,
  facilitatorReferencesFromForm,
  formatGroupRecurrenceLabel,
  hhmmToMinutes,
  joinGroupWithTemplate,
  listGroupFacilitatorOptions,
  requestGroupWaitlistFill,
  searchGroupTemplates,
  searchWaitlistForGroupTemplate,
  updateGroupTemplate,
} from '@/lib/groupTherapy';
import { usePracticeOrganization } from '@/pages/settings/template-library/hooks/usePracticeOrganization';

import { GroupAvatar } from './components/GroupAvatar';
import { GroupSetupFormModal } from './components/GroupSetupFormModal';
import { GroupTemplateFormModal } from './components/GroupTemplateFormModal';
import {
  NewCohortWaitlistFillModal,
  type WaitlistFillCandidate,
} from './components/NewCohortWaitlistFillModal';
import { useGroupTherapyGroups } from './hooks/useGroupTherapyGroups';

function enrollmentModeLabel(mode: GroupListItem['enrollmentMode']): string {
  return ENROLLMENT_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? mode;
}

const MODALITY_LABELS: Record<string, string> = {
  telehealth: 'Telehealth',
  'in-person': 'In person',
  hybrid: 'Hybrid',
  either: 'Either',
};

function modalityLabel(modality: string | undefined): string {
  if (!modality) return '—';
  return MODALITY_LABELS[modality] ?? modality.charAt(0).toUpperCase() + modality.slice(1);
}

function scheduleSummary(group: GroupListItem): string {
  if (!group.startDate) return 'Not scheduled';
  const recurrence = formatGroupRecurrenceLabel(group.recurrence);
  const start = hhmmToMinutes(group.startTime);
  const end = hhmmToMinutes(group.endTime);
  const durationMin =
    start != null && end != null && end > start ? end - start : undefined;
  const suffix = ` · ${group.startTime || '—'}${
    durationMin != null ? ` · ${durationMin} min` : ''
  }${group.timezone ? ` · ${group.timezone}` : ''}`;
  if (group.recurrence === 'custom') {
    const n = group.sessionDates?.length ?? 0;
    return `${recurrence} (${n} date${n === 1 ? '' : 's'})${suffix}`;
  }
  if (group.recurrence === 'daily' || group.recurrence === 'monthly') {
    return `${recurrence}${suffix}`;
  }
  const days = group.daysOfWeek.length ? group.daysOfWeek.join(', ') : '—';
  return `${days} · ${recurrence}${suffix}`;
}

export function GroupsPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { role } = usePermissions();
  const { organizationId: userOrganizationId, organization: userOrganization } =
    usePracticeOrganization();
  const showPracticeSelect = role === 'unify-admin' || role === 'unify-assistant';
  const defaultOrganizationId = showPracticeSelect
    ? undefined
    : userOrganizationId ?? undefined;
  const defaultOrganizationName = showPracticeSelect
    ? undefined
    : userOrganization?.name;

  const {
    groups,
    inactiveGroups,
    loading,
    error,
    reload,
    saveGroup,
    archiveGroup,
  } = useGroupTherapyGroups();

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [templateOpened, { open: openTemplate, close: closeTemplate }] = useDisclosure(false);

  const [activeTab, setActiveTab] = useState<string | null>('groups');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>('all');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateStatusFilter, setTemplateStatusFilter] = useState<string | null>('all');
  const [editGroup, setEditGroup] = useState<GroupListItem | null>(null);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState<GroupTemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState<GroupTemplateListItem | null>(null);

  const [practiceOptions, setPracticeOptions] = useState<{ value: string; label: string }[]>([]);
  const [providers, setProviders] = useState<
    { value: string; label: string; preferredLabel?: string; practiceId: string }[]
  >([]);
  const [waitlistFill, setWaitlistFill] = useState<{
    groupId: string;
    groupName: string;
    templateReference: string;
    capacity: number;
    candidates: WaitlistFillCandidate[];
  } | null>(null);

  const templateByRef = useMemo(() => {
    const map = new Map<string, GroupTemplateListItem>();
    for (const t of templates) map.set(t.reference, t);
    return map;
  }, [templates]);

  const reloadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const list = await searchGroupTemplates(
        medplum,
        showPracticeSelect ? undefined : defaultOrganizationId,
      );
      setTemplates(list);
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not load templates',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setTemplatesLoading(false);
    }
  }, [medplum, showPracticeSelect, defaultOrganizationId]);

  useEffect(() => {
    void reloadTemplates();
  }, [reloadTemplates]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [organizations, facilitators] = await Promise.all([
          medplum.searchResources('Organization', { type: 'prov', _count: '200' }),
          listGroupFacilitatorOptions(medplum),
        ]);
        if (cancelled) return;
        setPracticeOptions(
          organizations
            .filter((o) => o.id)
            .map((o) => ({ value: o.id as string, label: o.name ?? '' })),
        );
        setProviders(facilitators);
      } catch (e) {
        if (!cancelled) {
          notifications.show({
            color: 'red',
            title: 'Directory load failed',
            message: e instanceof Error ? e.message : String(e),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const allGroups = useMemo(
    () => [...groups, ...inactiveGroups],
    [groups, inactiveGroups],
  );

  const enrichedGroups = useMemo(
    () =>
      allGroups.map((g) =>
        joinGroupWithTemplate(
          g,
          g.templateReference ? templateByRef.get(g.templateReference) : undefined,
        ),
      ),
    [allGroups, templateByRef],
  );

  const visibleGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return enrichedGroups.filter((group) => {
      const matchesSearch =
        !query
        || group.name.toLowerCase().includes(query)
        || (group.templateDisplay ?? '').toLowerCase().includes(query);
      const status = group.lifecycleStatus ?? (group.active ? 'active' : 'inactive');
      const matchesStatus =
        !statusFilter || statusFilter === 'all' || status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [enrichedGroups, searchQuery, statusFilter]);

  const visibleTemplates = useMemo(() => {
    const query = templateSearchQuery.trim().toLowerCase();
    return templates.filter((template) => {
      const matchesSearch =
        !query
        || template.name.toLowerCase().includes(query)
        || (template.organizationName ?? '').toLowerCase().includes(query)
        || (template.comment ?? '').toLowerCase().includes(query);
      const status = template.active ? 'active' : 'inactive';
      const matchesStatus =
        !templateStatusFilter
        || templateStatusFilter === 'all'
        || status === templateStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [templates, templateSearchQuery, templateStatusFilter]);

  const handleArchive = useCallback(
    async (group: GroupListItem) => {
      if (!group.id) return;
      try {
        await archiveGroup(group.id);
        notifications.show({ color: 'green', message: 'Group archived.' });
      } catch (e) {
        notifications.show({
          color: 'red',
          title: 'Archive failed',
          message: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [archiveGroup],
  );

  const providerNameById = useMemo(
    () => new Map(providers.map((p) => [p.value, p.label])),
    [providers],
  );

  const columns = useMemo((): UnifyTableColumn<GroupListItem>[] => {
    // Group.participant refs may lack a display; resolve against the practitioner directory.
    const facilitatorNames = (g: GroupListItem): string[] =>
      facilitatorReferencesFromForm(g).map(
        (id, idx) => providerNameById.get(id) ?? g.facilitatorDisplays?.[idx] ?? id,
      );

    return [
      {
        key: 'name',
        label: 'Group',
        sortable: true,
        getValue: (g) => g.name,
        render: (g) => (
          <Group gap="sm" wrap="nowrap">
            <GroupAvatar imageUrl={g.imageUrl} groupId={g.id} size={40} />
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600} truncate>
                {g.name}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {g.organizationName ?? '—'}
                {g.templateDisplay ? ` · ${g.templateDisplay}` : ''}
              </Text>
            </Stack>
          </Group>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: 110,
        getValue: (g) => g.lifecycleStatus ?? (g.active ? 'active' : 'inactive'),
        render: (g) => {
          const status = g.lifecycleStatus ?? (g.active ? 'active' : 'inactive');
          return (
            <Badge variant="light" color={GROUP_LIFECYCLE_STATUS_COLORS[status]} size="sm">
              {GROUP_LIFECYCLE_STATUS_LABELS[status]}
            </Badge>
          );
        },
      },
      {
        key: 'mode',
        label: 'Mode',
        sortable: true,
        width: 100,
        getValue: (g) => g.enrollmentMode,
        render: (g) => (
          <Badge variant="light" color="violet" size="sm" style={{ whiteSpace: 'nowrap' }}>
            {enrollmentModeLabel(g.enrollmentMode)}
          </Badge>
        ),
      },
      {
        key: 'schedule',
        label: 'Schedule',
        getValue: (g) => scheduleSummary(g),
        render: (g) => (
          <Stack gap={2}>
            <Text size="sm">{scheduleSummary(g)}</Text>
            {g.startDate && (
              <Text size="xs" c="dimmed">
                {g.startDate}
                {g.endDate ? ` – ${g.endDate}` : g.enrollmentMode === 'open' ? ' – Open-ended' : ''}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'enrollment',
        label: 'Enrollment',
        sortable: true,
        width: 110,
        getValue: (g) => String(g.enrolledCount).padStart(4, '0'),
        render: (g) => (
          <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
            {g.enrolledCount} / {g.maxEnrollment}
          </Text>
        ),
      },
      {
        key: 'facilitators',
        label: 'Facilitators',
        width: 180,
        getValue: (g) => facilitatorNames(g).join(', '),
        render: (g) => {
          const names = facilitatorNames(g);
          return (
            <Text size="sm" lineClamp={2}>
              {names.length ? names.join(', ') : '—'}
            </Text>
          );
        },
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        pinRight: true,
        width: 56,
        render: (g) => (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                aria-label="Group actions"
                onClick={(e) => e.stopPropagation()}
              >
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconUserPlus size={14} />}
                onClick={() => g.id && navigate(`/dashboard/groups/${g.id}`)}
              >
                Manage members
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={() => {
                  setEditGroup(g);
                  openEdit();
                }}
              >
                Edit group
              </Menu.Item>
              {g.active && (
                <Menu.Item
                  leftSection={<IconArchive size={14} />}
                  color="orange"
                  onClick={() => void handleArchive(g)}
                >
                  Complete / End group
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ),
      },
    ];
  }, [
    handleArchive,
    navigate,
    openEdit,
    providerNameById,
  ]);

  const templateColumns = useMemo((): UnifyTableColumn<GroupTemplateListItem>[] => {
    return [
      {
        key: 'name',
        label: 'Template',
        sortable: true,
        getValue: (t) => t.name,
        render: (t) => (
          <Group gap="sm" wrap="nowrap">
            <GroupAvatar imageUrl={t.imageUrl} groupId={t.id} size={40} />
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600} truncate>
                {t.name}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {t.organizationName ?? '—'}
              </Text>
            </Stack>
          </Group>
        ),
      },
      {
        key: 'mode',
        label: 'Mode',
        width: 100,
        getValue: (t) => t.enrollmentMode,
        render: (t) => (
          <Badge variant="light" color="violet" size="sm">
            {enrollmentModeLabel(t.enrollmentMode)}
          </Badge>
        ),
      },
      {
        key: 'capacity',
        label: 'Capacity',
        width: 100,
        getValue: (t) => `${t.minEnrollment}-${t.maxEnrollment}`,
        render: (t) => (
          <Text size="sm">
            {t.minEnrollment}–{t.maxEnrollment}
          </Text>
        ),
      },
      {
        key: 'modality',
        label: 'Modality',
        width: 120,
        getValue: (t) => modalityLabel(t.modality),
        render: (t) => <Text size="sm">{modalityLabel(t.modality)}</Text>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 100,
        getValue: (t) => (t.active ? 'active' : 'inactive'),
        render: (t) => (
          <Badge variant="light" color={t.active ? 'green' : 'gray'} size="sm">
            {t.active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ];
  }, []);

  const createInitialValues = useMemo((): GroupSetupFormValues | undefined => {
    return {
      active: false,
      name: '',
      enrollmentMode: 'closed',
      additionalFacilitatorReferences: [],
      startDate: '',
      daysOfWeek: [],
      startTime: '18:00',
      endTime: '19:00',
      timezone: 'America/Los_Angeles',
      recurrence: 'weekly',
      sessionDates: [],
      sessionCount: 8,
      minEnrollment: 4,
      maxEnrollment: 10,
      organizationId: defaultOrganizationId,
      organizationName: defaultOrganizationName,
    };
  }, [defaultOrganizationId, defaultOrganizationName]);

  const editInitialValues = useMemo((): GroupSetupFormValues | undefined => {
    if (!editGroup) return undefined;
    return joinGroupWithTemplate(
      editGroup,
      editGroup.templateReference ? templateByRef.get(editGroup.templateReference) : undefined,
    );
  }, [editGroup, templateByRef]);

  const handleCreateSave = async (values: GroupSetupFormValues, activate: boolean) => {
    setSaving(true);
    try {
      const payload = { ...values, active: activate };
      const saved = await saveGroup(payload);
      notifications.show({
        color: 'green',
        message: activate ? 'Group activated and sessions generated.' : 'Group draft saved.',
      });
      closeCreate();
      setActiveTab('groups');

      if (
        activate
        && values.enrollmentMode === 'closed'
        && values.templateReference
        && saved.id
      ) {
        try {
          const queue = await searchWaitlistForGroupTemplate(medplum, values.templateReference);
          if (queue.length) {
            setWaitlistFill({
              groupId: saved.id,
              groupName: values.name,
              templateReference: values.templateReference,
              capacity: Math.max(0, values.maxEnrollment),
              candidates: queue.map((q) => ({
                id: q.id,
                clientName: q.clientName,
                authoredOn: q.authoredOn,
                patientId: q.patientId,
                serviceRequestId: q.serviceRequestId,
              })),
            });
          }
        } catch {
          // Waitlist fill is best-effort after create.
        }
      }
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async (values: GroupSetupFormValues, activate: boolean) => {
    setSaving(true);
    try {
      await saveGroup({ ...values, active: activate }, editGroup?.id);
      notifications.show({
        color: 'green',
        message: activate ? 'Group saved and sessions generated.' : 'Group draft saved.',
      });
      closeEdit();
      setEditGroup(null);
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateSave = async (
    values: GroupTemplateFormValues,
    imageFile?: File | null,
  ) => {
    setSaving(true);
    try {
      const isNew = !values.id;
      if (values.id) {
        await updateGroupTemplate(medplum, values, imageFile);
      } else {
        await createGroupTemplate(medplum, values, imageFile);
      }
      notifications.show({ color: 'green', message: 'Template saved.' });
      closeTemplate();
      setEditTemplate(null);
      if (isNew) setActiveTab('templates');
      await reloadTemplates();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Template save failed',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container size="xl" py="xl" data-testid="groups-page">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={2}>Groups</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text c="dimmed" size="sm">
              Group Templates define the offering; Groups are scheduled cohort instances.
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditTemplate(null);
                openTemplate();
              }}
            >
              Create template
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditGroup(null);
                openCreate();
              }}
              data-testid="create-group-button"
            >
              Create group
            </Button>
          </Group>
        </Group>

        {error && (
          <Alert color="red" title="Could not load groups">
            {error.message}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="groups">Groups</Tabs.Tab>
            <Tabs.Tab value="templates">Templates</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="groups" pt="md">
            {loading ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : (
              <Stack gap="md">
                <Group gap="md" wrap="wrap">
                  <SearchInput
                    placeholder="Search by name or template"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.currentTarget.value)}
                    w={280}
                  />
                  <Select
                    aria-label="Filter groups by status"
                    data={[...GROUP_LIFECYCLE_STATUS_OPTIONS]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    w={180}
                  />
                </Group>
                <Paper p="lg" withBorder>
                  <UnifyTable
                    data={visibleGroups}
                    columns={columns}
                    getRowKey={(g) => g.id ?? g.name}
                    sortable
                    withPaper={false}
                    scrollMinWidth={1100}
                    onRowClick={(g) => g.id && navigate(`/dashboard/groups/${g.id}`)}
                    emptyMessage="No groups yet. Create a template, then create a group."
                  />
                </Paper>
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="templates" pt="md">
            {templatesLoading ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : (
              <Stack gap="md">
                <Group gap="md" wrap="wrap">
                  <SearchInput
                    placeholder="Search by name or practice"
                    value={templateSearchQuery}
                    onChange={(event) => setTemplateSearchQuery(event.currentTarget.value)}
                    w={280}
                  />
                  <Select
                    aria-label="Filter templates by status"
                    data={[...GROUP_LIFECYCLE_STATUS_OPTIONS]}
                    value={templateStatusFilter}
                    onChange={setTemplateStatusFilter}
                    w={180}
                  />
                </Group>
                <Paper p="lg" withBorder>
                  <UnifyTable
                    data={visibleTemplates}
                    columns={templateColumns}
                    getRowKey={(t) => t.id ?? t.reference}
                    sortable
                    withPaper={false}
                    scrollMinWidth={900}
                    onRowClick={(t) => {
                      setEditTemplate(t);
                      openTemplate();
                    }}
                    emptyMessage="No templates yet. Create a Group Template to define offerings."
                  />
                </Paper>
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <GroupSetupFormModal
        opened={createOpened}
        title="Create group"
        initialValues={createInitialValues}
        templates={templates}
        providers={providers}
        saving={saving}
        onClose={closeCreate}
        onSubmit={handleCreateSave}
      />

      <GroupSetupFormModal
        opened={editOpened}
        title="Edit group"
        initialValues={editInitialValues}
        templates={templates}
        providers={providers}
        wasActive={editGroup?.active ?? false}
        saving={saving}
        onClose={() => {
          closeEdit();
          setEditGroup(null);
        }}
        onSubmit={handleEditSave}
      />

      <GroupTemplateFormModal
        opened={templateOpened}
        title={editTemplate ? 'Edit template' : 'Create template'}
        initialValues={
          editTemplate
            ?? (defaultOrganizationId
              ? {
                  active: true,
                  name: '',
                  enrollmentMode: 'closed',
                  minEnrollment: 4,
                  maxEnrollment: 10,
                  timezone: 'America/Los_Angeles',
                  modality: 'telehealth',
                  sessionCount: 8,
                  dayOfWeek: 'tue',
                  organizationId: defaultOrganizationId,
                  organizationName: defaultOrganizationName,
                }
              : undefined)
        }
        practiceOptions={practiceOptions}
        showPracticeSelect={showPracticeSelect}
        defaultOrganizationId={defaultOrganizationId}
        defaultOrganizationName={defaultOrganizationName}
        saving={saving}
        onClose={() => {
          closeTemplate();
          setEditTemplate(null);
        }}
        onSubmit={handleTemplateSave}
      />

      {waitlistFill && (
        <NewCohortWaitlistFillModal
          opened
          groupName={waitlistFill.groupName}
          capacity={waitlistFill.capacity}
          candidates={waitlistFill.candidates}
          onClose={() => setWaitlistFill(null)}
          onConfirm={async (selectedIds) => {
            try {
              await requestGroupWaitlistFill(medplum, waitlistFill.groupId, selectedIds);
              notifications.show({ color: 'green', message: 'Waitlist fill requested.' });
              setWaitlistFill(null);
              await reload();
            } catch (e) {
              notifications.show({
                color: 'red',
                title: 'Waitlist fill failed',
                message: e instanceof Error ? e.message : String(e),
              });
            }
          }}
        />
      )}
    </Container>
  );
}
